import { EASE_LUSION } from './easing';
import React from 'react';
import { motion } from 'framer-motion';

interface RevealLinesProps {
  lines: string[];
  className?: string;
  lineClassName?: string;
  delay?: number;
  once?: boolean;
}

/**
 * Lusion-style masked line reveal: each line slides up out of its own
 * overflow mask with a soft stagger. Calm — no bounce, no rotation drama.
 */
export const RevealLines: React.FC<RevealLinesProps> = ({
  lines,
  className = '',
  lineClassName = '',
  delay = 0,
  once = true,
}) => (
  <motion.span
    className={`block ${className}`}
    initial="hidden"
    whileInView="visible"
    viewport={{ once, margin: '-12% 0px' }}
    variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.09, delayChildren: delay } } }}
  >
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
          className={`block will-change-transform ${lineClassName}`}
          variants={{
            hidden: { y: '110%' },
            visible: { y: 0, transition: { duration: 0.9, ease: EASE_LUSION } },
          }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </motion.span>
);

interface FadeInProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  y?: number;
  once?: boolean;
}

/** Soft rise-and-fade for blocks of secondary content. */
export const FadeIn: React.FC<FadeInProps> = ({ children, className = '', delay = 0, y = 26, once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: '-10% 0px' }}
    transition={{ duration: 0.9, delay, ease: EASE_LUSION }}
  >
    {children}
  </motion.div>
);
